import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import {
  DVR_REQUEST_LIST_TABLE_COLUMNS,
  EDVR_REQUEST_LIST_TABLE_COLUMNS,
  EXTERNAL_EVENTS_LIST_TABLE_COLUMNS,
} from 'src/app/home/constants/home.constants';
import { GoogleTagManagerService, RequestedVideoType } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { IncidentModalComponent } from '@app-shared/components/incident-modal/incident-modal.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataService } from '@app-core/services/data/data.service';
import { DVR_STATUS_LIST, PAGE_SIZE_OPTIONS, VIDEO_REQUEST_PAGE_SIZE } from '@app-core/constants/constants';
import { StatusTimelineComponent } from '@app-shared/components/status-timeline/status-timeline.component';
import { DvrService } from '@app-dvr/services/dvr/dvr.service';
import { CLIENT_CONFIG } from '@config/config';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';
import { TranslateService } from '@ngx-translate/core';
import { DownloadService } from '@app-shared/services/common/download.service';

@Component({
  selector: 'app-video-requests',
  templateUrl: './video-requests.component.html',
  styleUrls: ['./video-requests.component.scss'],
})
export class VideoRequestsComponent implements AfterViewInit, OnDestroy {
  @ViewChild('paginator', { static: true })
  public paginator: MatPaginator;
  public pageSizeOptions: number[] = PAGE_SIZE_OPTIONS;

  @Input()
  get videoRequestType(): string {
    return this._videoRequestType;
  }
  set videoRequestType(requestType: string) {
    this._videoRequestType = requestType;
    if (requestType === 'edvr') {
      this.tableColumns = EDVR_REQUEST_LIST_TABLE_COLUMNS;
      this.swapColumns();
    } else if (requestType === 'dvr') {
      this.tableColumns = DVR_REQUEST_LIST_TABLE_COLUMNS;
      this.swapColumns();
    } else {
      this.tableColumns = EXTERNAL_EVENTS_LIST_TABLE_COLUMNS;
      this.swapColumns();
    }
  }

  @Input()
  get loader(): boolean {
    return this._loader;
  }
  set loader(loader: boolean) {
    this._loader = loader;
    this.tableSource.data = new Array(5).fill(undefined);
    this.paginator.firstPage();
  }

  @Input()
  get videoRequests(): any[] {
    return this._videoRequests;
  }
  set videoRequests(requests: any[]) {
    this._videoRequests = requests;
    if (!this.loader) {
      // To prevent resetting of dummy rows
      this.tableSource.data = requests;
    }
  }
  @Input()
  public timezone: any;
  @Input()
  public dateFormat: any;

  @Output()
  private pageChange = new EventEmitter<PageEvent>();

  public tableColumns = DVR_REQUEST_LIST_TABLE_COLUMNS;
  public dvrStatusList = DVR_STATUS_LIST;
  public clientConfig = CLIENT_CONFIG;
  public tableSource: MatTableDataSource<any> = new MatTableDataSource([]);
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private _loader: boolean;
  private _videoRequestType: string;
  private _videoRequests: any[];

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private gtmService: GoogleTagManagerService,
    public dataService: DataService,
    public dvrService: DvrService,
    private durationRangeService: DurationRangeService,
    public translate: TranslateService,
    private downloadService: DownloadService,
    private mixpanelService: MixPanelService
  ) {}
  public ngAfterViewInit() {
    this.paginator.page.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event: PageEvent) => {
      this.pageChange.emit(event);
    });
    this.tableSource.paginator = this.paginator;
  }

  public showMedia(positionIndex?: any) {
    if (this.videoRequestType === 'dvr' || this.videoRequestType === 'externalEvents') {
      this._videoRequests = this._videoRequests.map((x, index) => {
        if (this.videoRequestType === 'externalEvents') {
          return {
            ...x,
            isExternalEvent: this.videoRequestType === 'externalEvents',
          };
        }

        const { latitude = 0, longitude = 0 } = x.firstLocation || {};
        const mediaFiles = x.response?.mediaFiles || [];
        let isAudioEnabled = false;
        if (mediaFiles.length) {
          isAudioEnabled = mediaFiles[0]?.videoDetails?.audioEnabled || mediaFiles[1]?.videoDetails?.audioEnabled;
        }

        return {
          ...x,
          isDvrEvent: this.videoRequestType === 'dvr',
          latitude,
          longitude,
          eventVideoFilename: x.response.link,
          positionIndex: index,
          videoDetails: {
            videoResolution: x.videoResolution,
            audioEnabled: isAudioEnabled,
          },
        };
      });
    }
    const requestType = this.getRequestType(this.videoRequestType);
    this.mixpanelService.track('Clicked play video', {
      'Video Request Type': this.videoRequestType,
      'Request Type': requestType,
    });
    switch (requestType) {
      case 'DVR':
        this.gtmService.viewRequestedVideoFromTable('Video Requests');
        break;
      case 'E-DVR':
        this.gtmService.viewRequestedVideoFromTable('Event On-Demand');
        break;
      default:
        this.gtmService.viewRequestedVideoFromTable('Panic Button');
        break;
    }

    // modifying data for show only viewable pages
    const filteredDataForPagination = this.dataService.modifyDataBasedOnPageSize(
      this._videoRequests,
      positionIndex,
      VIDEO_REQUEST_PAGE_SIZE
    );
    const filteredDataPaginationIndex = this.dataService.modifyPageIndexBasedOnPageSize(positionIndex, VIDEO_REQUEST_PAGE_SIZE);

    const dialogRef = this.dialog.open(IncidentModalComponent, {
      panelClass: ['incident-modal', 'mobile-modal'],
      position: { top: '24px', bottom: '24px' },
      autoFocus: false,
      disableClose: true,
      data: {
        source: 'DVR',
        allEvents: filteredDataForPagination,
        currentIndex: filteredDataPaginationIndex,
        supportViewedStatus: true,
      },
    });

    dialogRef.afterClosed().subscribe(({ supportViewedStatus = false } = {}) => {
      if (supportViewedStatus) {
        this.tableSource.data = this._videoRequests.map((x, index) => {
          if (index === positionIndex) {
            return {
              ...x,
              isViewed: true,
            };
          }
          return x;
        });
      }
    });
  }

  public viewDvrStatusTimeline(request: any) {
    this.mixpanelService.track('Clicked on View Status Timeline');
    this.dialog.open(StatusTimelineComponent, {
      position: { top: '24px', bottom: '24px' },
      width: '640px',
      minHeight: '480px',
      autoFocus: false,
      panelClass: ['mobile-modal'],
      disableClose: true,
      data: request,
    });
  }

  public navigateTo(event: any) {
    this.router.navigate(['/trip-details'], {
      queryParams: {
        tripId: event.tripId,
        driverId: event.driverId,
      },
    });
    this.mixpanelService.track('Clicked trip details', {
      'Trip Id': event.tripId,
      'Driver Id': event.driverId,
    });
    const requestType = this.getRequestType(event);
    this.gtmService.gotoTripDetailsFromVideoRequestsTable(requestType);
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.unsubscribe();
  }

  private getRequestType(event: any) {
    if (this.videoRequestType === 'edvr') {
      return RequestedVideoType.edvr;
    }
    if (this.videoRequestType === 'dvr') {
      if (event.timelapseEnabled) {
        return RequestedVideoType.timelapseDvr;
      }
      return RequestedVideoType.dvr;
    }
  }

  public navigateToEditVideoRequest(request) {
    this.dvrService.localDVRDetails = request;
    this.router.navigate(['/video-requests/edit'], {
      queryParams: {
        uploadRequestId: request.uploadRequestId,
      },
    });
  }

  public onDownloadVideos(request: any): void {
    this.downloadService.onDownloadVideos(request);
  }

  private swapColumns() {
    this.tableColumns = this.clientConfig?.isAssetCentric
      ? this.tableColumns.map((column) => (column === 'driverId' ? 'assetId' : column === 'assetId' ? 'driverId' : column))
      : this.tableColumns;
    if (this.durationRangeService.planDetails?.featureFlags) {
      const featureFlags = this.durationRangeService.planDetails.featureFlags;
      if (!featureFlags.includes('tagging_workflow')) {
        this.tableColumns = this.tableColumns.filter((col) => col !== 'tags');
      }
    }
  }
}
